import * as React from 'react';

import Chip, { ChipProps } from '@mui/material/Chip';

import { ShippingStatus } from '../../graphql/API';

type StatusLabelProps = {
  type: StatusType;
  status?: string | null;
  emptyBackground?: boolean;
  fontWeight?: number;
};

type StatusType =
  | 'offer'
  | 'reservation'
  | 'invite'
  | 'userCategory'
  | 'post'
  | 'paymentStatus'
  | 'transactionStatus'
  | 'shippingStatus'
  | 'accessRequestStatus';

type StatusMapResult = { colorCode: ChipProps['color'] | string; text: string; border?: boolean };

type StatusMapDict = Record<string, StatusMapResult>;
type StatusMap = Record<string, StatusMapDict>;

type ColorResult = { color: string; backgroundColor: string };

type ColorMap = Record<Exclude<ChipProps['color'] | string, undefined>, ColorResult>;

type ChipStatusColor = Omit<StatusMapResult, 'colorCode'> & { colorMap: ColorResult };

const colorMap: ColorMap = {
  default: { color: 'black', backgroundColor: '' },
  primary: { color: '', backgroundColor: '#000' },
  secondary: { color: '#848484', backgroundColor: '#F6F6F6' },
  success: { color: '#37BB90', backgroundColor: '#B5EEDB' },
  warning: { color: '#955CCC', backgroundColor: '' },
  error: { color: '#DC5564', backgroundColor: '#FFE1E5' },
  info: { color: '#DBAF3E', backgroundColor: '#fff' },
  lightBlue: { color: '#486475', backgroundColor: '#D8EBFB' },
};

const statusMap: StatusMap = {
  offer: {
    active: { colorCode: 'success', text: 'Active' },
    cancelled: { colorCode: 'error', text: 'Cancelled' },
    pending: { colorCode: 'default', text: 'Pending' },
    rejected: { colorCode: 'error', text: 'Rejected' },
    deleted: { colorCode: 'error', text: 'Deleted' },
    ended: { colorCode: 'secondary', text: 'Ended' },
    inactive: { colorCode: 'secondary', text: 'Inactive' },
  },
  reservation: {
    cancelled: { colorCode: 'error', text: 'Cancelled' },
    checkedin: { colorCode: 'success', text: 'Checked In' },
    deleted: { colorCode: 'error', text: 'Deleted' },
    disputed: { colorCode: 'error', text: 'Disputed' },
    noshow: { colorCode: 'secondary', text: 'No Show' },
    noshowprocessed: { colorCode: 'secondary', text: 'No Show' },
    reserved: { colorCode: 'default', text: 'Reserved' },
    completed: { colorCode: 'success', text: 'Completed' },
  },
  invite: {
    accepted: { colorCode: 'success', text: 'Accepted' },
    declined: { colorCode: 'error', text: 'Declined' },
    expired: { colorCode: 'secondary', text: 'Expired', border: true },
    pending: { colorCode: 'secondary', text: 'Pending' },
    available: { colorCode: 'secondary', text: 'Pending' },
  },
  userCategory: {
    'BP LIFESTYLE': { colorCode: 'success', text: 'Lifestyle' },
    'BP SELECT': { colorCode: 'warning', text: 'Beautypass Select' },
  },
  post: {
    pending: { colorCode: 'default', text: 'Pending' },
    'in review': { colorCode: 'secondary', text: 'In Review' },
    approved: { colorCode: 'success', text: 'Approved' },
    rejected: { colorCode: 'error', text: 'Rejected' },
  },
  paymentStatus: {
    released: { colorCode: 'default', text: 'Released' },
    ready: { colorCode: 'default', text: 'Ready' },
    stop: { colorCode: 'error', text: 'Stop' },
    processed: { colorCode: 'success', text: 'Processed' },
  },
  transactionStatus: {
    pending: { colorCode: 'info', text: 'Pending' },
    complete: { colorCode: 'success', text: 'Complete' },
  },
  shippingStatus: {
    [ShippingStatus.waiting]: { colorCode: 'info', text: 'Waiting for shipment' },
    [ShippingStatus.shipped]: { colorCode: 'lightBlue', text: 'In Transit' },
    [ShippingStatus.cancelled]: { colorCode: 'secondary', text: 'Cancelled' },
    ['in-transit']: { colorCode: 'lightBlue', text: 'In Transit' },
    delivered: { colorCode: 'success', text: 'Delivered' },
  },
  accessRequestStatus: {
    accepted: { colorCode: 'success', text: 'Accepted' },
    declined: { colorCode: 'error', text: 'Rejected' },
    archived: { colorCode: 'success', text: 'Accepted' },
    expired: { colorCode: 'error', text: 'Expired' },
  },
};

const getDefault = (status: StatusType, value: string | undefined | null): ChipStatusColor => {
  if (!value) {
    return { colorMap: colorMap['secondary'], text: 'NA' };
  }

  const { colorCode, ...restProps } = statusMap[status][value] ?? {
    color: 'secondary',
    text: value,
  };

  return { colorMap: colorMap[colorCode ?? 'secondary'], ...restProps };
};

const StatusLabel: React.FC<StatusLabelProps> = ({
  type,
  status,
  emptyBackground,
  fontWeight = 500,
}) => {
  const { colorMap, text, border } = React.useMemo(() => getDefault(type, status), [type, status]);

  return (
    status && (
      <Chip
        size='small'
        label={text}
        variant={border ? 'outlined' : 'filled'}
        sx={{
          color: colorMap.color,
          backgroundColor: !border ? colorMap.backgroundColor : 'undefined',
          borderRadius: 0.5,
          fontWeight,
          fontSize: '15px',
          ...(emptyBackground
            ? {
                backgroundColor: 'unset',
                '& .MuiChip-label': {
                  padding: 0,
                },
              }
            : undefined),
        }}
      />
    )
  );
};

export default StatusLabel;
